/**
 * Single source of truth for the small set of value-coercion helpers used
 * across the action-editor stack (``ActionFieldEditor``,
 * ``envelope-primitives``, the share / picker primitives that bridge
 * scalar wire values to the chip-array shape).
 *
 * Lives in ``packages/ui/src/lib`` (zero internal deps) so it can be
 * imported by ANY component in ``@ando/ui`` without creating circular
 * imports — both ``ActionFieldEditor`` and the action-specific previews
 * that ride on its types depend on it without being aware of each other.
 *
 * Audit June 9, 2026 flagged a real semicolon-paste bug: two divergent
 * ``asStringArray`` implementations lived side-by-side — the envelope
 * version split on ``[,;]`` (handling pasted Outlook-style lists), the
 * editor version split on ``,`` only. Consolidating here closes the gap.
 */

import type { ActionFieldValue } from "../ando/ActionFieldEditor";

/**
 * Narrow a type-erased ``ActionFieldValue`` to ``string[]``.
 *
 * Accepts:
 *   - an already-canonical ``string[]`` (e.g. ``RECIPIENTS`` proposal),
 *   - a comma- OR semicolon-separated string (legacy ``MULTISELECT``
 *     proposals + raw pasted Outlook recipient lists like
 *     ``"a@x.com; b@y.com"``),
 *   - anything else → ``[]``.
 *
 * Filters empty / whitespace-only entries — the caller never has to
 * defend against accidental blank chips.
 */
export function asStringArray(value: ActionFieldValue | undefined): string[] {
  if (Array.isArray(value)) {
    return value.filter((s): s is string => typeof s === "string" && s.length > 0);
  }
  if (typeof value === "string" && value.trim() !== "") {
    return value
      .split(/[,;]/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

// ── Generic unknown → field-value coercion (flow surfaces, Aug 2026) ─────────
//
// The hive-flows surfaces (``ProposalReview``, ``NodeInspector``,
// ``ScopedChatPanel``) each grew a private copy of the same pure narrowing
// helpers. Consolidated here verbatim — semantics preserved from each origin.

/** A JSON-object-like value (node output, editable target, node config). */
export type PlainRecord = Record<string, unknown>;

/**
 * Narrow to a non-empty (non-whitespace) string, else ``undefined``.
 *
 * NOTE: deliberately DIFFERENT from the envelope stack's ``asString``
 * (``ando/actions/envelope-primitives``), which COERCES any value to a
 * display string (``null`` → ``""``, number → ``String(v)``). That coercing
 * variant stays local to the envelopes — merging the two would silently
 * change behavior on one side.
 */
export function asString(v: unknown): string | undefined {
  return typeof v === "string" && v.trim() ? v : undefined;
}

/** Type guard for a plain (non-array, non-null) object. */
export function isPlainRecord(v: unknown): v is PlainRecord {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** Coalescing sibling of ``isPlainRecord`` — anything non-record → ``{}``. */
export function asRecord(v: unknown): PlainRecord {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as PlainRecord) : {};
}

/** Editable-scalar guard (string | number | boolean). */
export function isScalar(v: unknown): v is string | number | boolean {
  return typeof v === "string" || typeof v === "number" || typeof v === "boolean";
}

/** ``string[]`` guard — every element must be a string. */
export function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((x) => typeof x === "string");
}

/**
 * Coerce an unknown node-OUTPUT value into an ``ActionFieldValue`` for the
 * review surface: nested dicts / arrays-of-objects become a readonly pretty
 * JSON preview (kept as a string, never sent back as an edit).
 *
 * NOT interchangeable with ``toActionFieldValue`` below — see its note.
 */
export function toFieldValue(value: unknown): ActionFieldValue {
  if (value === null || value === undefined) return null;
  if (isScalar(value) || isStringArray(value)) return value;
  // Nested dict / array-of-objects → readonly JSON preview (kept as a string,
  // never sent back as an edit).
  return JSON.stringify(value, null, 2);
}

/**
 * Coerce a node-CONFIG value into the ``ActionFieldValue`` the editor
 * expects: structured values (``file`` attachment chips, …) cross the
 * boundary AS-IS via the same documented cast the HITL editor uses
 * internally — unlike ``toFieldValue``, which stringifies them into a
 * read-only JSON preview. The two coercers are deliberately kept separate;
 * merging them would change one caller's behavior.
 */
export function toActionFieldValue(v: unknown): ActionFieldValue {
  if (v === null || v === undefined) return null;
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return v;
  if (Array.isArray(v) && v.every((x) => typeof x === "string")) return v as string[];
  return v as ActionFieldValue;
}
